import type { AttachmentResource } from "api/generated";
import type { Attachment } from "shared/types";

export const mapAttachment = (resource: AttachmentResource): Attachment => {
  const isVideo = resource.is_video;
  const isCompleted = resource.transcode_status === "completed";
  const hasStreamUrl = Boolean(resource.stream_url);

  return {
    id: resource.id,
    name: resource.name,
    attachment: resource.attachment,
    url: resource.url,
    previewUrl: resource.preview_url,
    mimetype: resource.mimetype,
    is_video: isVideo,
    transcode_status: resource.transcode_status,
    stream_url: resource.stream_url,
    thumbnail_url: resource.thumbnail_url,
    isPlayableVideo: isVideo && isCompleted && hasStreamUrl,
    isPendingOrFailedVideo:
      isVideo && (isCompleted === false || hasStreamUrl === false),
  };
};

/**
 * Reference-stable wrapper around `mapAttachment` for legacy code paths
 * (learning-objectives) that map raw API arrays at the component level
 * rather than at fetch time. Returns the SAME mapped array for the same
 * raw input array reference so consumers (e.g. AttachmentList) don't see
 * identity-thrashing on every parent render.
 *
 * Use only when the upstream store doesn't already map on fetch.
 */
const mappedAttachmentsCache = new WeakMap<
  readonly AttachmentResource[],
  Attachment[]
>();

export const mapAttachments = (
  resources: readonly AttachmentResource[] | null | undefined,
): Attachment[] => {
  if (!resources || resources.length === 0) {
    return [];
  }

  const cached = mappedAttachmentsCache.get(resources);
  if (cached !== undefined) {
    return cached;
  }

  const mapped = resources.map(mapAttachment);
  mappedAttachmentsCache.set(resources, mapped);
  return mapped;
};
